import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { useNavigate } from "react-router-dom";
import { app } from "../firebase"; // Ajuste o caminho conforme seu projeto
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  setDoc,
  doc
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { NumericFormat } from 'react-number-format';
import {
  LuTrash2,
  LuPlus,
  LuPencil,
  LuCheck,
  LuX,
  LuScissors,
  LuClock,
  LuLoaderCircle,
  LuImage as LuImageIcon,
  LuSave
} from "react-icons/lu";

// Se você tiver o ToastProvider, mantenha. Caso contrário, pode remover o hook.
// import { useToast } from "../components/ToastProvider"; 

// --- ANIMATIONS ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// --- STYLED COMPONENTS (LAYOUT GLOBAL) ---

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #3b82f6;
  /* Padrão de pontos idêntico ao anterior */
  background-image: radial-gradient(#76a9fc 1px, transparent 1px);
  background-size: 20px 20px;
  padding: 1rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  width: 100%;
  max-width: 700px;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh; /* Para permitir scroll interno se tiver muitos serviços */

  @media (min-width: 640px) {
    padding: 2.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;

  svg { color: #2563eb; }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
`;

// --- SERVICE CARD STYLES ---

const ScrollArea = styled.div`
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-bottom: 1rem;
  flex-grow: 1;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
`;

const ServiceItem = styled.div`
  background: white;
  border: 1px solid ${props => props.$editing ? '#3b82f6' : '#e5e7eb'};
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: all 0.2s;
  animation: ${fadeIn} 0.3s ease-out;
  box-shadow: ${props => props.$editing ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : '0 1px 3px rgba(0,0,0,0.05)'};

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

// Área da Imagem
const ImageUploader = styled.label`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 12px;
  background-color: #f3f4f6;
  border: 2px dashed ${props => props.$hasImage ? 'transparent' : '#d1d5db'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  overflow: hidden;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: ${props => props.$disabled ? '#d1d5db' : '#3b82f6'};
    background-color: ${props => props.$disabled ? '#f3f4f6' : '#eff6ff'};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Área de Conteúdo (Inputs ou Texto)
const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`;

// Inputs Estilizados
const InputBaseStyles = css`
  width: 100%;
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    background: white;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StyledInput = styled.input`
  ${InputBaseStyles}
`;

const StyledSelect = styled.select`
  ${InputBaseStyles}
  cursor: pointer;
`;

// Wrapper para o NumericFormat (já que ele é um componente customizado)
const NumericWrapper = styled.div`
  input {
    ${InputBaseStyles}
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// Exibição (Modo Leitura)
const ServiceInfo = styled.div`
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.25rem;
  }
  
  div {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #4b5563;
    font-size: 0.95rem;

    span {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }
`;

// Botões de Ação (Editar/Excluir/Salvar)
const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
  justify-content: flex-end;

  @media (max-width: 639px) {
    width: 100%;
    margin-top: 0.5rem;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  /* Variantes */
  ${props => props.$variant === 'primary' && css`
    background-color: #eff6ff;
    color: #2563eb;
    &:hover { background-color: #dbeafe; }
  `}

  ${props => props.$variant === 'danger' && css`
    background-color: #fef2f2;
    color: #ef4444;
    &:hover { background-color: #fee2e2; }
  `}

  ${props => props.$variant === 'success' && css`
    background-color: #2563eb;
    color: white;
    &:hover { background-color: #1d4ed8; }
  `}

  ${props => props.$variant === 'ghost' && css`
    background-color: transparent;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    &:hover { background-color: #f3f4f6; }
  `}
`;

const AddButtonCard = styled.button`
  width: 100%;
  border: 2px dashed #d1d5db;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }
`;

const Footer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const MainButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 12px;
  border: none;
  background-color: ${props => props.disabled ? '#93c5fd' : '#2563eb'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);

  &:hover {
    background-color: ${props => props.disabled ? '#93c5fd' : '#1d4ed8'};
    transform: ${props => props.disabled ? 'none' : 'scale(0.99)'};
  }
`;

const SpinnerIcon = styled(LuLoaderCircle)`
  animation: ${spin} 1s linear infinite;
`;


// --- COMPONENT ---

export default function CadastroServico() {
  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);
  const navigate = useNavigate();
  // const { success: toastSuccess, error: toastError } = useToast(); // Se usar toast

  const [subdominio, setSubdominio] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [isSalvando, setIsSalvando] = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);

  // Inicialização e busca do subdomínio
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const q = query(collection(db, "barbearia"), where("criadorUid", "==", user.uid));
        const snap = await getDocs(q);

        if (!snap.empty) {
          setSubdominio(snap.docs[0].id);
        } else {
          // Fallback se não achar
          console.warn("Nenhuma barbearia encontrada.");
          navigate("/cadastro-barbearia");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingInit(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db, navigate]);

  // --- CRUD Lógica ---

  const adicionarServico = () => {
    setServicos([
      ...servicos,
      { 
        idTemp: Date.now(), // Ajuda na renderização do React
        nome: "", 
        valor: "", 
        tempo: "30", 
        file: null, 
        preview: null, 
        editando: true 
      }
    ]);
  };

  const removerServico = (index) => {
    const novos = [...servicos];
    novos.splice(index, 1);
    setServicos(novos);
  };

  const handleChange = (index, field, value) => {
    const novos = [...servicos];
    novos[index][field] = value;
    setServicos(novos);
  };

  const handleImageChange = (index, file) => {
    if (!file) return;
    const novos = [...servicos];
    novos[index].file = file;
    novos[index].preview = URL.createObjectURL(file);
    setServicos(novos);
  };

  const confirmarEdicao = (index) => {
    const s = servicos[index];
    if (!s.nome || s.valor === "" || isNaN(parseFloat(s.valor))) {
      alert("Preencha o nome e um valor válido.");
      return;
    }
    const novos = [...servicos];
    novos[index].editando = false;
    setServicos(novos);
  };

  const editarServico = (index) => {
    const novos = [...servicos];
    novos[index].editando = true;
    setServicos(novos);
  };

  const cancelarEdicao = (index) => {
    const s = servicos[index];
    // Se estava criando um novo e cancelou sem preencher nada relevante, remove
    const isNovoEVazio = !s.nome && (!s.valor || s.valor === "") && !s.file;
    
    if (isNovoEVazio) {
      removerServico(index);
    } else {
      // Se era edição de existente, ou tinha dados, apenas fecha modo edição (aqui simplificado)
      // Idealmente reverteria para o estado anterior, mas para simplicidade:
      if (!s.nome) { 
          removerServico(index); // Remove se não tiver nome ao cancelar
      } else {
          const novos = [...servicos];
          novos[index].editando = false;
          setServicos(novos);
      }
    }
  };

  const uploadImagem = async (file, nomeServico) => {
    const ext = file.name.split('.').pop();
    const fileName = `${nomeServico.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.${ext}`;
    const imageRef = ref(storage, `${subdominio}/servicos/${fileName}`);
    await uploadBytes(imageRef, file);
    return await getDownloadURL(imageRef);
  };

  const salvarTudo = async () => {
    if (!subdominio) return;
    if (isSalvando) return;

    // Validações
    const emEdicao = servicos.some(s => s.editando);
    if (emEdicao) {
      alert("Confirme a edição de todos os serviços (clique no check) antes de salvar.");
      return;
    }
    if (servicos.length === 0) {
      alert("Adicione pelo menos um serviço.");
      return;
    }

    try {
      setIsSalvando(true);
      const servicoIds = [];

      // Loop para salvar cada serviço
      for (const servico of servicos) {
        let imageUrl = "";
        if (servico.file) {
          imageUrl = await uploadImagem(servico.file, servico.nome);
        }

        const docRef = await addDoc(collection(db, `barbearia/${subdominio}/servico`), {
          nome: servico.nome,
          valor: parseFloat(servico.valor),
          tempo: parseInt(servico.tempo),
          imagem: imageUrl || "",
          ativo: true
        });
        servicoIds.push(docRef.id);
      }

      // Atualizar Barbeiro Proprietário
      const user = auth.currentUser;
      if (user) {
        const barbeiroRef = doc(db, `barbearia/${subdominio}/barbeiros`, user.uid);
        await setDoc(barbeiroRef, { servicosIds: servicoIds }, { merge: true });
      }

      // Atualizar Status da Barbearia
      const barbeariaRef = doc(db, "barbearia", subdominio);
      await setDoc(barbeariaRef, {
        SituacaoCadastral: { statusServico: "ok" }
      }, { merge: true });

      // alert("Serviços salvos!"); 
      // Se tiver toast: toastSuccess("Serviços salvos!");

      navigate("/cadastro-barbeiro"); // Próxima tela (exemplo)

    } catch (error) {
      console.error("Erro ao salvar:", error);
      alert("Erro ao salvar serviços.");
    } finally {
      setIsSalvando(false);
    }
  };

  if (loadingInit) {
    return (
      <Container>
        <Card style={{ alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <SpinnerIcon style={{ fontSize: '2rem', color: '#2563eb' }} />
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Header>
          <Title><LuScissors /> Cadastro de Serviços</Title>
          <Subtitle>Quais serviços você oferece?</Subtitle>
        </Header>

        <ScrollArea>
          {servicos.map((servico, index) => (
            <ServiceItem key={servico.idTemp || index} $editing={servico.editando}>
              
              {/* UPLOAD IMAGEM */}
              <input
                type="file"
                accept="image/*"
                id={`file-${index}`}
                style={{ display: "none" }}
                onChange={(e) => {
                  if (servico.editando) handleImageChange(index, e.target.files[0]);
                }}
              />
              <ImageUploader
                htmlFor={`file-${index}`}
                $disabled={!servico.editando}
                $hasImage={!!servico.preview}
              >
                {servico.preview ? (
                  <img src={servico.preview} alt="Serviço" />
                ) : (
                  <LuImageIcon style={{ color: '#9ca3af', fontSize: '1.5rem' }} />
                )}
                {servico.editando && !servico.preview && (
                    <div style={{position: 'absolute', bottom: 4, fontSize: '0.6rem', color: '#9ca3af'}}>FOTO</div>
                )}
              </ImageUploader>

              {/* CONTEÚDO */}
              <ContentArea>
                {servico.editando ? (
                  <>
                    <div>
                        <Label>Nome do Serviço</Label>
                        <StyledInput
                        placeholder="Ex: Corte Degradê"
                        value={servico.nome}
                        onChange={(e) => handleChange(index, "nome", e.target.value)}
                        />
                    </div>

                    <Row>
                      <div style={{ flex: 1 }}>
                        <Label>Valor</Label>
                        <NumericWrapper>
                            <NumericFormat
                            customInput={StyledInput}
                            value={servico.valor}
                            onValueChange={(values) => handleChange(index, "valor", values.floatValue)}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            placeholder="R$ 0,00"
                            />
                        </NumericWrapper>
                      </div>
                      <div style={{ width: '100px' }}>
                        <Label>Tempo</Label>
                        <StyledSelect
                          value={servico.tempo}
                          onChange={(e) => handleChange(index, "tempo", e.target.value)}
                        >
                          {[15, 30, 45, 60, 75, 90, 105, 120].map((min) => (
                            <option key={min} value={min}>{min} min</option>
                          ))}
                        </StyledSelect>
                      </div>
                    </Row>

                    <ActionButtons>
                      <IconButton $variant="ghost" onClick={() => cancelarEdicao(index)}>
                        <LuX /> Cancelar
                      </IconButton>
                      <IconButton $variant="success" onClick={() => confirmarEdicao(index)}>
                        <LuCheck /> Confirmar
                      </IconButton>
                    </ActionButtons>
                  </>
                ) : (
                  <>
                    <ServiceInfo>
                      <h3>{servico.nome}</h3>
                      <div>
                        <span style={{ fontWeight: 600, color: '#2563eb' }}>
                          R$ {parseFloat(servico.valor).toFixed(2).replace('.', ',')}
                        </span>
                        <span><LuClock size={14}/> {servico.tempo} min</span>
                      </div>
                    </ServiceInfo>
                    
                    <ActionButtons>
                      <IconButton $variant="primary" onClick={() => editarServico(index)}>
                        <LuPencil /> Editar
                      </IconButton>
                      <IconButton $variant="danger" onClick={() => removerServico(index)}>
                        <LuTrash2 /> Excluir
                      </IconButton>
                    </ActionButtons>
                  </>
                )}
              </ContentArea>
            </ServiceItem>
          ))}

          <AddButtonCard onClick={adicionarServico}>
            <LuPlus size={20} /> Adicionar Novo Serviço
          </AddButtonCard>

        </ScrollArea>

        <Footer>
          {servicos.length > 0 && (
            <MainButton onClick={salvarTudo} disabled={isSalvando}>
              {isSalvando ? (
                <>
                    <SpinnerIcon /> Salvando...
                </>
              ) : (
                <>
                    Finalizar e Salvar <LuSave />
                </>
              )}
            </MainButton>
          )}
        </Footer>

      </Card>
    </Container>
  );
}